import { useState, useEffect, useCallback, useRef } from 'react';
import { myApi } from '../../../assets/utils/lib';
import { notification } from 'antd';
import { PENDING_ONBOARDING_POLICY_TAB } from '../orgDetailTabHelpers';

/**
 * Custom hook for managing organization policies with the unified V2 endpoint
 * Supports: Active, Inactive, Pending Onboarding, and Export tabs
 */
export const POLICY_DATA_TABS = ['active', 'inactive', PENDING_ONBOARDING_POLICY_TAB];

export const usePolicies = (socialNo, initialTab = 'active') => {
  const resolvedInitialTab = POLICY_DATA_TABS.includes(initialTab) ? initialTab : 'active';
  const latest = useRef({
    activeTab: resolvedInitialTab,
    search: '',
    pagination: { page: 1, limit: 20, total: 0, pages: 0, has_next: false, has_prev: false }
  });
  const fetchPoliciesRef = useRef(null);
  const skipSearchDebounceOnce = useRef(true);

  const [activeTab, setActiveTab] = useState(resolvedInitialTab);
  const [data, setData] = useState([]);
  const [tabCounts, setTabCounts] = useState({
    active: { count: 0, label: 'Active Policies', description: '' },
    inactive: { count: 0, label: 'Inactive Policies', description: '' },
    pending_onboarding: { count: 0, label: 'Pending Onboarding', description: '' },
    export: { count: 0, label: 'Export CSV', description: '' }
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false
  });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  latest.current = { activeTab, search, pagination };

  const fetchPolicies = useCallback(async (tab, searchQuery, page, limit) => {
    if (!socialNo) {
      console.warn('usePolicies: socialNo is required');
      return;
    }

    const tabVal = tab ?? latest.current.activeTab;
    const searchVal = searchQuery ?? latest.current.search;
    const pageVal = page ?? latest.current.pagination.page;
    const limitVal = limit ?? latest.current.pagination.limit;

    // Don't fetch for export tab or non-policy tabs
    if (tabVal === 'export' || !POLICY_DATA_TABS.includes(tabVal)) return;

    setLoading(true);
    setError(null);

    try {
      const params = {
        tab: tabVal,
        page: pageVal,
        limit: limitVal,
        ...(searchVal && { search: searchVal })
      };

      const response = await myApi('GET', `/v2/corporations/${socialNo}/policies/`, params);
      const result = response.data;

      if (result.error === 0) {
        setData(result.data || []);
        setTabCounts(prev => result.tabs || prev);
        setPagination(prev => result.pagination || prev);
      } else {
        setError(result.message || 'Failed to fetch policies');
        notification.error({
          message: 'Error Loading Policies',
          description: result.message || 'Failed to fetch policies',
          placement: 'topRight'
        });
        setData([]);
      }
    } catch (err) {
      console.error('usePolicies error:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to fetch policies';
      setError(errorMsg);
      notification.error({
        message: 'Network Error',
        description: errorMsg,
        placement: 'topRight'
      });
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [socialNo]);

  fetchPoliciesRef.current = fetchPolicies;

  // Load when org or tab changes (pagination is driven by user actions + handlers below)
  useEffect(() => {
    if (socialNo && activeTab !== 'export' && POLICY_DATA_TABS.includes(activeTab)) {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchPolicies(activeTab, latest.current.search, 1, latest.current.pagination.limit);
    }
  }, [socialNo, activeTab, fetchPolicies]);

  // Debounced refetch when search text changes only — never on pagination/tab (avoids resetting page)
  useEffect(() => {
    if (skipSearchDebounceOnce.current) {
      skipSearchDebounceOnce.current = false;
      return;
    }

    const timeoutId = setTimeout(() => {
      const tab = latest.current.activeTab;
      if (tab === 'export' || !POLICY_DATA_TABS.includes(tab)) return;
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchPoliciesRef.current(tab, search, 1, latest.current.pagination.limit);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [search]);

  // Export function
  const exportPolicies = async (exportTab = 'all', searchQuery = search) => {
    if (!socialNo) return null;

    setLoading(true);
    try {
      const params = {
        tab: 'export',
        export_tab: exportTab,
        ...(searchQuery && { search: searchQuery })
      };

      const response = await myApi('GET', `/v2/corporations/${socialNo}/policies/`, params);
      const result = response.data;

      console.log('Export API Response:', result);

      if (result.error === 0) {
        const exportData = result.data;
        console.log('Export Data:', exportData);
        
        notification.success({
          message: 'Export Successful',
          description: `Export ready: ${exportData.total_records || 0} record(s)`,
          placement: 'topRight',
          duration: 3
        });
        return exportData;
        
      } else {
        notification.error({
          message: 'Export Failed',
          description: result.message || 'Failed to export policies',
          placement: 'topRight'
        });
        return null;
      }
    } catch (err) {
      console.error('Export error:', err);
      notification.error({
        message: 'Export Error',
        description: err.response?.data?.message || err.message || 'Failed to export policies',
        placement: 'topRight'
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Change tab handler — only reset search when the tab actually changes
  const changeTab = useCallback((newTab) => {
    if (!POLICY_DATA_TABS.includes(newTab)) return;
    if (newTab === latest.current.activeTab) return;
    setActiveTab(newTab);
    setSearch('');
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  // Pagination: Ant Design calls onChange(page, pageSize) for both page and page-size changes.
  // If onShowSizeChange also runs, a follow-up onChange can fire with the OLD limit still in refs;
  // handling pageSize here only avoids that race (e.g. 20 → 10 being overwritten by a fetch with limit 20).
  const goToPage = (page, pageSize) => {
    const { activeTab: tab, search: q, pagination: pag } = latest.current;
    const prevLimit = pag.limit;
    const nextLimit =
      pageSize !== undefined && pageSize !== null ? Number(pageSize) : prevLimit;
    const limitChanged = nextLimit !== prevLimit;

    if (limitChanged) {
      setPagination(prev => ({ ...prev, limit: nextLimit, page: 1 }));
      fetchPolicies(tab, q, 1, nextLimit);
    } else {
      setPagination(prev => ({ ...prev, page }));
      fetchPolicies(tab, q, page, nextLimit);
    }
  };

  return {
    // State
    activeTab,
    data,
    tabCounts,
    pagination,
    search,
    loading,
    error,

    // Actions
    setActiveTab: changeTab,
    setSearch,
    fetchPolicies,
    refetch: () => fetchPolicies(activeTab, search, pagination.page, pagination.limit),
    exportPolicies,
    goToPage
  };
};

export default usePolicies;

